import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor } from "../../../../types/color";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { ConciergeHomeBrandLabelState } from "./types";

/**
 * Load a Google Font stylesheet into document.head.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: ConciergeHomeBrandLabelState = {
  text: "CONCIERGE BY",
  fontSize: 14,
  fontWeight: "medium",
  fontFamily: "inherit",
  color: themeColor("textPrimary"),
  letterSpacing: 2,
  textTransform: "uppercase",
  marginBottom: 8,
  mobile_fontSize: 14,
  mobile_marginBottom: 8,
};

const editFields: EditField[] = [
  { key: "text", label: "Label Text", type: "text" },
  {
    key: "fontSize",
    label: "Font Size",
    type: "number",
    min: 10,
    max: 24,
    step: 1,
    responsive: true,
  },
  {
    key: "fontWeight",
    label: "Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "fontFamily",
    label: "Font Family",
    type: "font",
  },
  { key: "color", label: "Color", type: "color" },
  {
    key: "letterSpacing",
    label: "Letter Spacing",
    type: "number",
    min: 0,
    max: 10,
    step: 0.5,
  },
  {
    key: "textTransform",
    label: "Text Transform",
    type: "options",
    options: [
      { label: "None", value: "none" },
      { label: "Uppercase", value: "uppercase" },
      { label: "Lowercase", value: "lowercase" },
      { label: "Capitalize", value: "capitalize" },
    ],
  },
  {
    key: "marginBottom",
    label: "Spacing Below",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
    responsive: true,
  },
];

interface ConciergeHomeBrandLabelWidgetProps {
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ConciergeHomeBrandLabelState>;
}

export const ConciergeHomeBrandLabelWidget: React.FC<
  ConciergeHomeBrandLabelWidgetProps
> = ({
  widgetId = "concierge-home-brand-label",
  displayName = "Brand Label",
  initialState = {},
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergeHomeBrandLabelState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Resolve font family
  const fontKey = state.fontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading) {
    return <></>;
  }

  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : "inherit";

  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  const uniqueClass = `brand-label-${widgetId || "default"}`;
  const mobileFontSize = (state as any).mobile_fontSize ?? state.fontSize;
  const mobileMarginBottom = (state as any).mobile_marginBottom ?? state.marginBottom;

  const fontStyleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      fontFamilyCss !== "inherit"
        ? `.${uniqueClass} { font-family: ${fontFamilyCss} !important; }`
        : ""
    }
    .${uniqueClass}-container {
      margin-bottom: ${state.marginBottom}px;
    }
    .${uniqueClass}-text {
      font-size: ${state.fontSize}px;
    }
    @media (max-width: 767px) {
      .${uniqueClass}-container {
        margin-bottom: ${mobileMarginBottom}px !important;
      }
      .${uniqueClass}-text {
        font-size: ${mobileFontSize}px !important;
      }
    }
  `.trim();

  const fontWeightValue =
    state.fontWeight === "normal"
      ? 400
      : state.fontWeight === "medium"
        ? 500
        : state.fontWeight === "semibold"
          ? 600
          : 700;

  const content = (
    <>
      {fontStyleCss && <style>{fontStyleCss}</style>}
      <div
        className={`${uniqueClass}-container ${fontFamilyCss !== "inherit" ? uniqueClass : ""}`}
        style={{
          textAlign: "center",
        }}
      >
        <span
          className={`${uniqueClass}-text`}
          style={{
            fontWeight: fontWeightValue,
            color: resolveColor(state.color),
            letterSpacing: state.letterSpacing,
            textTransform: state.textTransform as any,
          }}
        >
          {state.text}
        </span>
      </div>
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {content}
    </WidgetWrapper>
  );
};
